import { useEffect, useRef } from "react";

import { FloatingPortal } from "@floating-ui/react";
import {
  faChevronDown,
  faChevronRight,
  faTableLayout,
} from "@fortawesome/pro-regular-svg-icons";
import { AnimatePresence, m } from "motion/react";
import { NavLink, useLocation } from "react-router-dom";
import { twJoin } from "tailwind-merge";
import { useLocalStorage } from "usehooks-ts";

import { PopoverProvider } from "src/base-components/Popover/PopoverContext";
import { useCaseTemplatesWithVersions } from "src/cases/queries/templates";
import { CaseTemplateModal } from "src/cases/templates/CaseTemplateModal";
import { TemplateNavList } from "src/cases/templates/TemplateNavList";
import { Icon } from "src/design-system/Icon";
import { useModal } from "src/design-system/Modal";
import {
  HoverPopoverLockProvider,
  useHoverPopover,
} from "src/hooks/useHoverPopover";
import { buildNavLinkClickHandler } from "src/layout/navLinkClick";
import {
  getUrlToCaseTemplatesPage,
  getUrlToCaseTemplateVersionsPage,
} from "src/router/urls";

type CaseTemplatesNavSectionProps = {
  collapsed: boolean;
  orgId: string;
  wsId: string;
  canCreate: boolean;
};

export const CaseTemplatesNavSection: React.FC<
  CaseTemplatesNavSectionProps
> = ({ collapsed, orgId, wsId, canCreate }) => {
  const { pathname } = useLocation();
  const [isExpanded, setIsExpanded] = useLocalStorage(
    "caseTemplatesSidebarExpanded",
    false,
  );

  const {
    isOpen: isEditModalOpen,
    data: editingTemplateId,
    openModal: openEditModal,
    closeModal: closeEditModal,
    afterLeave: editModalAfterLeave,
  } = useModal<string>();

  const { data: templates, isLoading: isLoadingTemplates } =
    useCaseTemplatesWithVersions();

  const isOnTemplatesPage = pathname.includes("/templates");

  // Auto-expand when navigating to a templates page.
  const prevIsOnTemplatesPageRef = useRef(false);
  useEffect(() => {
    if (isOnTemplatesPage && !prevIsOnTemplatesPageRef.current) {
      prevIsOnTemplatesPageRef.current = true;
      setIsExpanded(true);
    } else if (!isOnTemplatesPage && prevIsOnTemplatesPageRef.current) {
      prevIsOnTemplatesPageRef.current = false;
    }
  }, [isOnTemplatesPage, setIsExpanded]);

  const {
    isOpen: hoverOpen,
    refs: hoverRefs,
    floatingStyles: hoverFloatingStyles,
    getReferenceProps: getHoverReferenceProps,
    getFloatingProps: getHoverFloatingProps,
    lockApi: hoverLockApi,
  } = useHoverPopover({ enabled: collapsed });

  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsExpanded(!isExpanded);
  };

  const firstTemplateUrl = templates?.[0]
    ? getUrlToCaseTemplateVersionsPage(orgId, wsId, templates[0].id)
    : getUrlToCaseTemplatesPage(orgId, wsId);

  const showInlineList = !collapsed && isExpanded;

  const templateNavListProps = {
    canCreate,
    isLoading: isLoadingTemplates,
    onEditRequest: (id: string) => openEditModal(id),
    orgId,
    templates,
    wsId,
  } as const;

  return (
    <>
      <li
        ref={hoverRefs.setReference}
        className={twJoin(
          "flex shrink cursor-default flex-col overflow-x-visible",
          showInlineList && templates && templates.length > 3
            ? "min-h-[192px]"
            : "min-h-min",
        )}
        {...getHoverReferenceProps()}
      >
        <div>
          <NavLink
            aria-label={collapsed ? "Case templates" : undefined}
            className={({ isActive }) =>
              twJoin(
                "mr-2.5 flex shrink-0 items-center gap-x-2 rounded-lg",
                collapsed ? "p-1.5" : "py-1.5 pr-2 pl-1.5",
                (isOnTemplatesPage || isActive) && "bg-gray-100",
                "hover:bg-gray-50",
              )
            }
            data-loc="case-templates"
            end={false}
            to={firstTemplateUrl}
            onClick={buildNavLinkClickHandler(isOnTemplatesPage)}
          >
            <Icon icon={faTableLayout} size="xs" />
            {!collapsed && (
              <>
                <span className="w-full font-inter-medium-13px text-gray-800">
                  Case templates
                </span>
                <div onClick={handleToggleClick}>
                  <Icon
                    icon={isExpanded ? faChevronDown : faChevronRight}
                    size="xs"
                  />
                </div>
              </>
            )}
          </NavLink>
        </div>
        {!collapsed && (
          <AnimatePresence initial={false}>
            {isExpanded && (
              <m.div
                key="template-list"
                animate={{ height: "auto", opacity: 1 }}
                className="overflow-hidden pt-2"
                exit={{ height: 0, opacity: 0 }}
                initial={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                <TemplateNavList {...templateNavListProps} displayAsNested />
              </m.div>
            )}
          </AnimatePresence>
        )}
      </li>
      {collapsed && (
        <FloatingPortal>
          <div
            ref={hoverRefs.setFloating}
            className={twJoin(
              "z-40 w-60 rounded-lg border border-gray-100 bg-white p-3 shadow-base",
              !hoverOpen && "hidden",
            )}
            data-loc="case-templates-hover-popover"
            style={hoverFloatingStyles}
            {...getHoverFloatingProps({
              onClick: (e) => e.stopPropagation(),
            })}
          >
            <HoverPopoverLockProvider value={hoverLockApi}>
              <PopoverProvider>
                <div className="mb-2 px-1.5 font-inter-semibold-13px text-gray-800">
                  Case templates
                </div>
                <TemplateNavList {...templateNavListProps} />
              </PopoverProvider>
            </HoverPopoverLockProvider>
          </div>
        </FloatingPortal>
      )}
      <CaseTemplateModal
        afterLeave={editModalAfterLeave}
        open={isEditModalOpen}
        templateId={editingTemplateId}
        onClose={closeEditModal}
      />
    </>
  );
};
